// Impact view: (derivedContext × function) heatmap derived from a project's
// requirements. Cell color = MAX required level across skills in the
// cell; cell text = total demand count (multi-project pile-up surfaces
// concentration risk). Aggregate mode adds per-project attribution to
// the tooltip.

import type { BusinessFunction } from '@lib/api-types/business-functions';
import type { RatingValue } from '@lib/api-types/ratings';
import { Fragment, useMemo } from 'react';

import { EmptyState } from '../../components/EmptyState.tsx';
import { MatrixCell } from '../../components/MatrixCell.tsx';
import {
  ColumnHeader,
  GroupedCompLabel,
  NestConnector,
  PropIndicator,
  TruncatedLabel,
  nestVariant,
} from '../../components/TruncatedLabel.tsx';
import {
  RATING_LABELS,
  buildSkillFunctions,
  compRowHeight,
  isCompactCatalog,
  isDefaultVisibleSkill,
  isInstitutional,
} from '../../data-views.ts';
import type {
  MergedDerivedContext,
  MergedSkill,
  ProjectImpactCell,
  ProjectRole,
  ProjectView,
} from '../../roadmap-data.ts';

import {
  compServesFunctionWithNew,
  isNewFunction,
  reqKey,
  skillCellServesFunction,
} from './req-key.ts';

interface SkillRequirement {
  level: RatingValue;
  roles: Set<string>;
}

export function RoadmapImpactGrid({
  project,
  grouped,
  showLibraries,
}: {
  project: ProjectView;
  grouped: boolean;
  showLibraries: boolean;
}) {
  const { mergedDerivedContexts, mergedFunctions, requirementsByRole, roles } =
    project;

  // Per-skill requirement summary used by flat (non-grouped) mode. For
  // aggregate ProjectViews the top-level `roles` + `requirementsByRole`
  // are empty (per-project breakdown lives in `projectGroups`), so we
  // derive from `maxRequirements`; the role set stays empty there since
  // attribution belongs to the tooltip rather than the cell.
  // Keyed by composite reqKey(comp, skill) — requirements are scoped per
  // derivedContext, so a shared skill can carry a different bar under each.
  const requirementBySkill = useMemo(() => {
    const map = new Map<string, SkillRequirement>();
    if (project.isAggregate && project.maxRequirements) {
      // maxRequirements is per-skill (supply-side aggregate); fan it out to
      // every derivedContext that contains the skill so composite lookups resolve.
      for (const comp of mergedDerivedContexts) {
        for (const s of comp.skills) {
          const level = project.maxRequirements[s.id];
          if (level === undefined) continue;
          map.set(reqKey(comp.id, s.id), { level, roles: new Set() });
        }
      }
      return map;
    }
    for (const role of roles) {
      const reqs = requirementsByRole[role.id] ?? {};
      for (const [key, req] of Object.entries(reqs)) {
        const entry = map.get(key);
        if (!entry) {
          map.set(key, { level: req.level, roles: new Set([role.id]) });
        } else {
          if (req.level > entry.level) entry.level = req.level;
          entry.roles.add(role.id);
        }
      }
    }
    return map;
  }, [project, requirementsByRole, roles, mergedDerivedContexts]);

  // Show the full platform map — every visible derivedContext × every
  // function. Demand cells light up in the teal ramp; intersections
  // the roadmap doesn't touch get the light-teal `lvl-0` "0" cell.
  // Library skills are filtered out by default to match Resilience /
  // Talent / Resource (frameworks, services, and institutional skills
  // are the load-bearing ones at the platform level).
  const visibleComps = useMemo(
    () =>
      mergedDerivedContexts
        .map(c => ({
          ...c,
          skills: showLibraries
            ? c.skills
            : c.skills.filter(isDefaultVisibleSkill),
        }))
        .filter(c => c.skills.length > 0),
    [mergedDerivedContexts, showLibraries]
  );

  const skillFunctions = useMemo(
    () => buildSkillFunctions(mergedDerivedContexts),
    [mergedDerivedContexts]
  );

  const skillDisplayById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of mergedDerivedContexts) {
      for (const s of c.skills) map.set(s.id, s.display);
    }
    return map;
  }, [mergedDerivedContexts]);

  const gridStyle = useMemo(() => {
    const base: Record<string, string> = {
      gridTemplateColumns: `130px 42px 6px 140px repeat(${mergedFunctions.length}, minmax(72px, 1fr))`,
    };
    if (grouped) {
      const compact = isCompactCatalog(mergedDerivedContexts);
      const heights = visibleComps
        .map(c => `${compRowHeight(c.skills.length, compact)}px`)
        .join(' ');
      base.gridTemplateRows = `auto ${heights}`;
    }
    return base;
  }, [mergedFunctions.length, grouped, visibleComps, mergedDerivedContexts]);

  if (visibleComps.length === 0 || mergedFunctions.length === 0) {
    return (
      <EmptyState
        title='Nothing to map yet'
        description='This snapshot has no contexts or functions.'
      />
    );
  }

  const isAggregate = project.isAggregate === true;

  return (
    <div className='snap-grid' style={gridStyle}>
      <div />
      <div />
      <div />
      <div />
      {mergedFunctions.map(fn => (
        <ColumnHeader
          key={fn.id}
          display={fn.display}
          subtitle={fn.subtitle}
        />
      ))}

      {grouped
        ? visibleComps.map(comp => (
            <Fragment key={comp.id}>
              <GroupedCompLabel
                propCount={comp.skills.filter(isInstitutional).length}
                name={
                  <TruncatedLabel
                    text={comp.display}
                    max={22}
                    isNew={comp.is_overlay}
                  />
                }
                count={
                  <>
                    {comp.skills.length} skill
                    {comp.skills.length === 1 ? '' : 's'}
                  </>
                }
              />
              {mergedFunctions.map(fn => (
                <ImpactCell
                  key={fn.id}
                  comp={comp}
                  fn={fn}
                  cell={project.impactGrid[comp.id]?.[fn.id]}
                  compServesFn={compServesFunctionWithNew(
                    comp,
                    fn.id,
                    skillFunctions,
                    project.newSkillFunctions,
                  )}
                  skillDisplayById={skillDisplayById}
                  isAggregate={isAggregate}
                  projectDisplay={project.display}
                />
              ))}
            </Fragment>
          ))
        : visibleComps.map(comp => (
            <Fragment key={comp.id}>
              {comp.skills.map((skill, i) => (
                <Fragment key={skill.id}>
                  <div className='snap-grid__row-label snap-grid__row-label--muted'>
                    {i === 0 ? (
                      <TruncatedLabel
                        text={comp.display}
                        isNew={comp.is_overlay}
                      />
                    ) : null}
                  </div>
                  <div className='snap-grid__row-label snap-grid__row-label--conn'>
                    <NestConnector variant={nestVariant(i, comp.skills.length)} />
                  </div>
                  <div className='snap-grid__row-label snap-grid__row-label--prop'>
                    <PropIndicator institutional={isInstitutional(skill)} />
                  </div>
                  <div className='snap-grid__row-label'>
                    <TruncatedLabel text={skill.display} isNew={skill.is_overlay} />
                  </div>
                  {mergedFunctions.map(fn => (
                    <RequirementCell
                      key={fn.id}
                      skill={skill}
                      fn={fn}
                      roles={roles}
                      requirement={requirementBySkill.get(reqKey(comp.id, skill.id)) ?? null}
                      appliesToFunction={skillCellServesFunction(
                        comp,
                        skill.id,
                        fn.id,
                        skillFunctions,
                        project.newSkillFunctions,
                      )}
                      isNewFunction={isNewFunction(
                        comp.id,
                        skill.id,
                        fn.id,
                        project.newSkillFunctions,
                      )}
                      rowIsOverlay={comp.is_overlay || skill.is_overlay}
                    />
                  ))}
                </Fragment>
              ))}
            </Fragment>
          ))}
    </div>
  );
}

/**
 * Grouped-mode cell. Reads the precomputed `impactGrid[comp.id][fn.id]`
 * directly: color = MAX required level, text = total demand count.
 * Tooltip body lists each demand as `<skill> · <level> (<role>) — <project>`
 * (project name only when rendering an aggregate ProjectView).
 */
function ImpactCell({
  comp,
  fn,
  cell,
  compServesFn,
  skillDisplayById,
  isAggregate,
  projectDisplay,
}: {
  comp: MergedDerivedContext;
  fn: BusinessFunction;
  cell: ProjectImpactCell | undefined;
  compServesFn: boolean;
  skillDisplayById: Map<string, string>;
  isAggregate: boolean;
  projectDisplay: string;
}) {
  const overlayClass = comp.is_overlay ? ' is-overlay-cell' : '';
  // True N/A: the comp doesn't serve this function. Render the muted
  // grey treatment so users can tell "no intersection" apart from
  // "intersection exists but this roadmap doesn't touch it."
  if (!compServesFn) {
    return (
      <MatrixCell
        value={null}
        levelClass='na'
        tooltip={null}
        extraClass={`is-merged${overlayClass}`}
      />
    );
  }
  // Intersection exists but the active roadmap puts no demand on it —
  // surface a light-teal "0" so the cell still reads as part of the
  // platform map (just unconsumed).
  if (!cell || cell.skillCount === 0) {
    return (
      <MatrixCell
        value={0}
        levelClass='purple lvl-0'
        extraClass={`is-merged${overlayClass}`}
        tooltip={{
          title: `${comp.display} · ${fn.display}`,
          body: 'Not impacted by the active roadmap.',
        }}
      />
    );
  }

  const level = cell.maxLevel as RatingValue;
  const items = isAggregate
    ? (cell.contributions ?? [])
    : cell.demands.map(d => ({ project: projectDisplay, ...d }));

  const lines = items
    .map(it => {
      const skillDisplay = skillDisplayById.get(it.skill) ?? it.skill;
      const roleNote = it.role && it.role !== 'primary' ? ` (${it.role})` : '';
      const projNote = isAggregate ? ` — ${it.project}` : '';
      return `  ${skillDisplay} · ${RATING_LABELS[it.level]}${roleNote}${projNote}`;
    })
    .join('\n');

  const body = [
    `Strictest bar: ${RATING_LABELS[level]}`,
    `${cell.count} profile${cell.count === 1 ? '' : 's'} required · ${cell.skillCount} skill-demand${cell.skillCount === 1 ? '' : 's'}`,
    lines,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <MatrixCell
      value={cell.count}
      levelClass={`purple lvl-${level}`}
      extraClass={`is-merged${overlayClass}`}
      tooltip={{
        title: `${comp.display} · ${fn.display}`,
        body,
      }}
    />
  );
}

function RequirementCell({
  skill,
  fn,
  roles,
  requirement,
  appliesToFunction,
  isNewFunction: isNew,
  rowIsOverlay,
}: {
  skill: MergedSkill;
  fn: BusinessFunction;
  roles: ProjectRole[];
  requirement: SkillRequirement | null;
  appliesToFunction: boolean;
  /** This (skill × function) was assigned beyond the catalog footprint. */
  isNewFunction: boolean;
  rowIsOverlay: boolean;
}) {
  // A net-new (beyond-catalog) association gets the same "newly-added"
  // treatment as a freshly-added skill/derivedContext row.
  const overlayClass = rowIsOverlay || isNew ? 'is-overlay-cell' : undefined;
  if (!appliesToFunction) {
    return (
      <MatrixCell
        value={null}
        levelClass='na'
        tooltip={null}
        extraClass={overlayClass}
      />
    );
  }
  if (!requirement) {
    return (
      <MatrixCell
        value={0}
        levelClass='purple lvl-0'
        extraClass={overlayClass}
        tooltip={{
          title: `${skill.display} · ${fn.display}`,
          body: 'Not impacted by the active roadmap.',
        }}
      />
    );
  }
  const matched = roles.filter(r => requirement.roles.has(r.id));
  return (
    <MatrixCell
      value={requirement.level}
      levelClass={`purple lvl-${requirement.level}`}
      extraClass={overlayClass}
      tooltip={{
        title: `${skill.display} · ${fn.display}`,
        body:
          `Required: ${RATING_LABELS[requirement.level]}\n` +
          `Roles: ${matched.map(r => r.display).join(', ') || '—'}`,
      }}
    />
  );
}
